import { DatePipe } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { BusinessService } from '@app/business/business.service';
import { InfoService } from '@app/core/info/info.service';
import { SessionStorageService } from '@app/core/local-storage/local-storage.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'cloud-business-registered-company-users',
  templateUrl: './business-registered-company-users.component.html',
  styleUrls: ['./business-registered-company-users.component.scss']
})
export class BusinessRegisteredCompanyUsersComponent implements OnInit {
  @ViewChild(MatPaginator, { static: false }) paginator: MatPaginator;
  loadingResend = true;
  formReport: FormGroup;
  isSubmitting: boolean = false;
  selectRoles: string[] = [
    'MY_BTRUST_ADMINISTRATOR',
    'MY_BTRUST_USER',
    'MY_BTRUST_SUPER_ADMINISTRATOR'
  ];

  roleLabels: Record<string, string> = {
    MY_BTRUST_ADMINISTRATOR: 'Administrator',
    MY_BTRUST_USER: 'User',
    MY_BTRUST_SUPER_ADMINISTRATOR: 'Super Administrator'
  };

  selectServices: any;
  reportDataSource: MatTableDataSource<any>;

  displayedColumns: string[] = ['number', 'name', 'position', 'role', 'service', 'registrationDate', 'status'];
  companyCmrId: any;
  totalCountPages: any;
  countUsers: any;
  offset: number = 0;
  limit: number = 10;
  serviceTypeLabels: { [key: string]: string } = {};
  currentDate = new Date();
  registrationDateOrder: string | null = null;
  statusesLabels: { value: string, label: string }[] = [
    { value: 'ACTIVE', label: 'reportsTabTexts.statuses.active' },
    { value: 'BLOCKED', label: 'reportsTabTexts.statuses.blocked' },
    { value: 'DELETED', label: 'reportsTabTexts.statuses.deleted' },
  ];

  constructor(
    private formBuilder: FormBuilder,
    private sessionStorageService: SessionStorageService,
    private businessService: BusinessService,
    public info: InfoService,
    private translateService: TranslateService,
  ) { }

  ngOnInit(): void {
    this.companyCmrId = this.sessionStorageService.getItem("businessAccountId");
    this.selectRoles.forEach(role => {
      this.roleLabels[role] = this.translateService.instant(`reportsTabTexts.roles.${role}`);
    });
    this.businessService.getServices(this.companyCmrId).subscribe(services => {
      this.selectServices = services.data;
      this.loadServiceTypeLabels();
    });
    this.initialForm()
    this.getAllRoles()
    this.getAllReports()
  }

  loadServiceTypeLabels(): void {
    if (!this.selectServices || this.selectServices.length === 0) {
      console.warn('selectServices is undefined or empty');
      return;
    }

    this.selectServices?.forEach?.(service => {
      this.translateService.get(`reportsTabTexts.serviceTypes.${service.serviceType}`).subscribe(translated => {
        this.serviceTypeLabels[service.serviceType] = translated;
      });
    });
  }
  checkLoadedResend() {
    return this.loadingResend;
  }

  hasData(): boolean {
    return (this.reportDataSource && this.reportDataSource.data.length !== 0);
  }

  initialForm() {
    this.formReport = this.formBuilder.group({
      role: [[]],
      service: [[]],
      status: [[]],
      fromDate: [null],
      toDate: [null]
    });
  }

  get fromDateValue() {
    return this.formReport.get('fromDate')?.value;
  }

  get toDateValue() {
    return this.formReport.get('toDate')?.value;
  }

  get fromDateMax() {
    const toDate = this.toDateValue;
    return toDate && toDate < this.currentDate ? toDate : this.currentDate;
  }

  clearField(fieldName: string) {
    this.formReport.get(fieldName)?.reset();
  }

  onCheckboxChange(controlName: string, option: string, checked: boolean) {
    const selected = this.formReport.value[controlName] as string[];
    if (checked) {
      this.formReport.get(controlName)?.setValue([...selected, option]);
    } else {
      this.formReport.get(controlName)?.setValue(selected.filter(o => o !== option));
    }
  }

  onPaginateChange(event: PageEvent) {
    this.offset = event.pageIndex
    this.limit = event.pageSize;
    this.getAllReports();
  }

  sortData(sort: Sort) {
    if (sort.active === 'registrationDate' && sort.direction) {
      this.registrationDateOrder = sort.direction.toUpperCase();
    } else {
      this.registrationDateOrder = null;
    }
    this.submitFormReport();
  }

  submitFormReport() {
    this.isSubmitting = false;
    const formValues = { ...this.formReport.value };
    const searchCriteria = {
      ...formValues,
      offset: this.offset,
      limit: this.limit,
      fromDate: formValues.fromDate ? new DatePipe(this.translateService.currentLang).transform(formValues.fromDate, 'dd.MM.yyyy') : null,
      toDate: formValues.toDate ? new DatePipe(this.translateService.currentLang).transform(formValues.toDate, 'dd.MM.yyyy') : null,
      registrationDateOrder: this.registrationDateOrder
    };
    this.businessService.getReportsUsersCompany(searchCriteria, this.companyCmrId).subscribe((response: any) => {
      this.reportDataSource = new MatTableDataSource(response.body?.data);
      this.totalCountPages = response.body?.count
    })
  }
  get lang() {
    return this.translateService.currentLang;
  }
  getName(e) {
    if (e !== null) {
      return this.lang === 'bg' ? e.fullNameBg : e.fullNameEn;
    } else {
      return '';
    }

  }
  getAllRoles() {
    this.businessService.getUserRoles().subscribe((response: any) => {
      this.selectRoles = response.roles
    })
  }

  getAllServiceTypesFlat(row: any): string[] {
    return row.groups?.flatMap(group => group.serviceTypes) || [];
  }
  getAllReports() {
    const formValues = { ...this.formReport.value };
    const searchCriteria = {
      ...formValues,
      offset: this.offset,
      limit: this.limit
    };
    this.businessService.getReportsUsersCompany(searchCriteria, this.companyCmrId).subscribe((response: any) => {
      this.reportDataSource = new MatTableDataSource(response.body?.data);
      this.totalCountPages = response.body?.count
      this.countUsers = response.body?.count ?? 0;
    })
  }

  exportExcel() {
    if (!this.hasData()) {
      this.info.error(this.translateService.instant('certificates.noDataExport'));
      return;
    }
    const formValues = { ...this.formReport.value };
    const searchCriteria = {
      ...formValues,
      offset: this.offset,
      limit: this.limit
    };

    this.businessService.getReportInExcelFormat(searchCriteria, this.companyCmrId)
      .subscribe({
        next: (response) => {
          try {
            const base64File = response?.body?.fileContent;
            const fileName = response?.body?.filename || 'Portal_Report.xlsx';

            if (base64File) {
              this.downloadBase64File(base64File, fileName);
            } else {
              this.info.error(this.translateService.instant('general.downloadError'));
            }
          } catch (error) {
            console.error('Download error:', error);
            this.info.error(this.translateService.instant('general.downloadError'));
          }
        },
        error: () => {
          this.info.error(this.translateService.instant('general.downloadError'));
        }
      });
  }

  private downloadBase64File(base64: string, fileName: string): void {
    try {
      const byteCharacters = atob(base64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setTimeout(() => URL.revokeObjectURL(url), 100);
    } catch (error) {
      console.error('Base64 decoding error:', error);
      this.info.error(this.translateService.instant('general.downloadError'));
    }
  }

}
